import { createContext, useContext, useState, ReactNode } from "react";
import userService from "../shared/api-services/user.service";
import { IUser } from "../shared/api-services/types";
import AuthService from "../shared/services/AuthService";

interface UserContextType {
  user: IUser | null;
  fetchUserDetails: (
    onSuccess?: FetchUserSuccessCallback,
    onError?: FetchUserErrorCallback
  ) => Promise<void>;
  logout: () => void;
  updateUser: (newUser: IUser) => void;
}

type FetchUserSuccessCallback = (data: IUser) => void;
type FetchUserErrorCallback = (error: any) => void;

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);

  const fetchUserDetails = async (
    onSuccess?: FetchUserSuccessCallback,
    onError?: FetchUserErrorCallback
  ): Promise<void> => {
    try {
      const data = await userService.user();
      setUser(data);
      data.role && sessionStorage.setItem("role", data.role);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      if (onError) onError(err);
    }
  };

  const updateUser = (newUser: IUser) => {
    setUser(newUser);
  };

  const logout = async () => {
    setUser(null);
    await AuthService.logout();
  };

  return (
    <UserContext.Provider
      value={{
        user,
        fetchUserDetails,
        updateUser,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
